import { cn } from "@/lib/utils";
import { Loader2, AlertTriangle } from "lucide-react";

export function PageHeader({ title, subtitle, overline, actions, icon: Icon }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
      <div>
        {overline && <p className="overline text-teal mb-1" data-testid="page-overline">{overline}</p>}
        <div className="flex items-center gap-3">
          {Icon && <Icon className="h-6 w-6 text-navy" strokeWidth={2} />}
          <h1 className="font-serif-display text-3xl sm:text-4xl tracking-tight text-navy" data-testid="page-title">{title}</h1>
        </div>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

const STATUS_MAP = {
  Active: "bg-emerald-100 text-emerald-800",
  Approved: "bg-emerald-100 text-emerald-800",
  Resolved: "bg-emerald-100 text-emerald-800",
  Present: "bg-emerald-100 text-emerald-800",
  "Half Day": "bg-amber-100 text-amber-800",
  Rejected: "bg-red-100 text-red-800",
  Cancelled: "bg-slate-200 text-slate-700",
  Withdrawn: "bg-slate-200 text-slate-700",
  Valid: "bg-emerald-100 text-emerald-800",
  Compliant: "bg-emerald-100 text-emerald-800",
  Competent: "bg-emerald-100 text-emerald-800",
  Probation: "bg-amber-100 text-amber-800",
  Notice: "bg-amber-100 text-amber-800",
  Expiring: "bg-amber-100 text-amber-800",
  Pending: "bg-amber-100 text-amber-800",
  Pending_RO: "bg-amber-100 text-amber-800",
  Pending_HR: "bg-amber-100 text-amber-800",
  Pending_SA: "bg-amber-100 text-amber-800",
  Work_Approved: "bg-teal/15 text-teal",
  Pending_Backup: "bg-amber-100 text-amber-800",
  Backup_Rejected: "bg-red-100 text-red-800",
  PendingVerification: "bg-amber-100 text-amber-800",
  Calculated: "bg-amber-100 text-amber-800",
  Locked: "bg-navy/10 text-navy",
  Inactive: "bg-slate-200 text-slate-700",
  Archived: "bg-slate-200 text-slate-700",
  Left: "bg-slate-200 text-slate-700",
  Terminated: "bg-red-100 text-red-800",
  Retired: "bg-slate-200 text-slate-700",
  Expired: "bg-red-100 text-red-800",
  "Non-compliant": "bg-red-100 text-red-800",
  // Projects & Service Providers
  Submitted: "bg-amber-100 text-amber-800",
  Verified: "bg-emerald-100 text-emerald-800",
  Draft: "bg-amber-100 text-amber-800",
  Paid: "bg-teal/15 text-teal",
  Completed: "bg-navy/10 text-navy",
  Closed: "bg-slate-200 text-slate-700",
};

const STATUS_LABELS = {
  Pending_RO: "Pending · Reporting Officer",
  Pending_HR: "Pending · HR",
  Pending_SA: "Pending · Super Admin",
  Work_Approved: "Approved · credited after you work",
  Pending_Backup: "Pending · Backup",
  Backup_Rejected: "Backup declined · choose another",
  PendingVerification: "Pending Verification",
};

export function StatusBadge({ status }) {
  const cls = STATUS_MAP[status] || "bg-slate-100 text-slate-700";
  return (
    <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold", cls)} data-testid={`status-${status}`}>
      {STATUS_LABELS[status] || status || "—"}
    </span>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="flex items-center justify-center py-20 text-slate-400" data-testid="loading-state">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> {label}
    </div>
  );
}

// Shown when a query fails (timeout, network error, server error) instead
// of leaving the caller stuck on <Loading/> forever - always pair with the
// query's `refetch` so the user has a real way forward, not a dead end.
export function ErrorState({ message = "Something went wrong.", onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-red-200 rounded-lg bg-red-50/40" data-testid="error-state">
      <AlertTriangle className="h-8 w-8 text-red-400 mb-3" />
      <p className="font-semibold text-slate-700">{message}</p>
      <p className="text-sm text-slate-400 mt-1 max-w-sm">This usually clears up on its own - please try again.</p>
      {onRetry && (
        <button onClick={onRetry} className="mt-4 text-sm font-medium text-teal hover:underline" data-testid="error-retry-btn">
          Try again
        </button>
      )}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-slate-200 rounded-lg bg-white" data-testid="empty-state">
      {Icon && <Icon className="h-10 w-10 text-slate-300 mb-3" />}
      <p className="font-semibold text-slate-700">{title}</p>
      {hint && <p className="text-sm text-slate-400 mt-1 max-w-sm">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
